import { CardShell } from '@atlas/ui-components'
import { Chip, cn, MenuContent, MenuItem, MenuTrigger, MenuTriggerButton } from '@atlas/ui-primitives'
import { X } from 'lucide-react'
import { Eyebrow } from '@/components/editorial/PageHeader'

/** One facet group: an axis and the (already cross-filtered) values it offers. */
export interface RuleFacetGroup {
	axis: string
	label: string
	values: string[]
	/** Dynamically-added registry axes can be removed; the built-in ones cannot. */
	removable?: boolean
}

export interface RuleAxisOption {
	axis: string
	label: string
}

export interface RuleFilterRailProps {
	groups: RuleFacetGroup[]
	/** Active values per axis (multi-select: a rule matches if its value is any of these). */
	selected: Record<string, string[]>
	onToggle: (axis: string, value: string) => void
	/** Registry axes not yet shown as a facet group (offered by "＋ Add variable"). */
	availableAxes?: RuleAxisOption[]
	onAddAxis?: (axis: string) => void
	onRemoveAxis?: (axis: string) => void
}

/**
 * Left facet rail for the Rules Explorer (design §8.13). Multi-select per group (a
 * rule matches an axis when its value is any of the selected ones); cross-filtering
 * (skip-self) is computed by the page and passed in as `groups`. "＋ Add variable"
 * adds any registry axis (dimension or attribute) as a new, removable facet group.
 * Presentational + storied.
 */
export function RuleFilterRail({
	groups,
	selected,
	onToggle,
	availableAxes = [],
	onAddAxis,
	onRemoveAxis,
}: RuleFilterRailProps) {
	const canAdd = !!onAddAxis && availableAxes.length > 0
	const addVariableCls =
		'mt-1 w-full rounded-ds-md border border-dashed border-ds-border bg-ds-surface p-2 text-left text-xs leading-tight text-ds-text-muted disabled:opacity-50'

	return (
		<CardShell as="aside" className="p-4">
			<Eyebrow className="mb-3">Filter rail</Eyebrow>
			{groups.map((g, i) => (
				<div key={g.axis} className={cn('mb-3.5', i > 0 && 'border-t border-ds-border pt-3')}>
					<div className="mb-2 flex items-center justify-between">
						<span className="text-xs font-semibold text-ds-text">{g.label}</span>
						{g.removable && onRemoveAxis ? (
							<button
								type="button"
								onClick={() => onRemoveAxis(g.axis)}
								aria-label={`Remove ${g.label} filter`}
								className="text-ds-text-subtle hover:text-ds-text"
							>
								<X className="h-3 w-3" />
							</button>
						) : null}
					</div>
					<div className="flex flex-wrap gap-1.5">
						{g.values.map((v) => {
							const active = (selected[g.axis] ?? []).includes(v)
							return (
								<Chip
									key={v}
									as="button"
									type="button"
									variant={active ? 'primary' : 'muted'}
									size="sm"
									onClick={() => onToggle(g.axis, v)}
									aria-pressed={active}
								>
									{v}
								</Chip>
							)
						})}
					</div>
				</div>
			))}
			{canAdd ? (
				<MenuTrigger>
					<MenuTriggerButton className={addVariableCls}>
						＋ Add variable
						<span className="block font-mono text-ds-4xs text-ds-text-subtle">dimension or attribute</span>
					</MenuTriggerButton>
					<MenuContent className="w-56">
						{availableAxes.map((a) => (
							<MenuItem key={a.axis} id={a.axis} onAction={() => onAddAxis?.(a.axis)}>
								{a.label}
							</MenuItem>
						))}
					</MenuContent>
				</MenuTrigger>
			) : (
				<button type="button" disabled className={addVariableCls}>
					＋ Add variable
					<span className="block font-mono text-ds-4xs text-ds-text-subtle">dimension or attribute</span>
				</button>
			)}
		</CardShell>
	)
}
